/**
 * Welche Art wartet wo (Spec, User Story 47, erweitert).
 *
 * Eine Zeile je **Warteschlange**, und eine Warteschlange ist das Paar aus
 * Endpunkt und aufgelöstem Basismodell: Das ist die Einheit, an der ADR-0003 den
 * Slot festmacht. Ein globaler Zähler sagt, dass etwas wartet; diese Fläche sagt,
 * wo — und damit, ob eine Slotzahl zu schärfen ist oder ein Agent freizugeben.
 *
 * Sie steht über der Liste der liegengebliebenen Aufträge, weil es dieselbe Frage
 * in zwei Schärfen ist: *kommt das System hinterher* und *was ist steckengeblieben*.
 *
 * Drei Zeilenformen kommen im Betrieb vor:
 *
 * - **Endpunkt und Basismodell** — die gewöhnliche Warteschlange.
 * - **„noch nicht aufgelöst"** — der Agent hat sein Preset noch nicht aufgelöst
 *   (ADR-0005); es zählt allein der Endpunkt-Wert, geraten wird nichts.
 * - **„Kontrolle (ohne Modell)"** — sie gehört zu keinem Agenten, ruft kein Modell
 *   und braucht keinen Slot (ADR-0010). Bei den Slots steht deshalb ein
 *   Gedankenstrich und keine Null: Eine Null behauptete, ihr fehle Kapazität.
 */

import type { ReactElement, ReactNode } from "react";

import { Chip, DataTable } from "../../design-system";
import { REVIEW_JOB_KINDS } from "../../api/client";
import type { AdminApi, QueueLoad } from "../../api/client";
import { formatNumber } from "../../formatting";
import { NumericCell, StrongCell } from "../cells";
import { ListSection } from "../list";
import { Notice, useLoaded } from "../loading";
import { KIND_LABELS } from "../reviewJobs";

export function QueueLoadSection({
  client,
}: {
  // Nur der eine Weg, den dieser Abschnitt greift. Die Rollenprüfung hängt nicht
  // daran, sondern am Pfad: `/api/admin/*` sieht ausschließlich `admin`.
  client: Pick<AdminApi, "queueLoad">;
}): ReactElement {
  const { state } = useLoaded(() => client.queueLoad());

  const queues = state.kind === "loaded" ? state.value : [];
  const waiting = queues.reduce((total, queue) => total + amountOf(queue.pending), 0);

  return (
    <ListSection
      title="Warteschlangenstand"
      glyph="list-checks"
      first
      headerRight={
        state.kind === "loaded" ? <Chip>{`${formatNumber(waiting)} wartend`}</Chip> : null
      }
    >
      <div style={{ padding: "var(--sp-2) var(--sp-5)" }}>
        {state.kind === "loading" ? <Notice text="Wird geladen ..." glyph="timer" /> : null}
        {state.kind === "failed" ? (
          <Notice text={state.message} glyph="unplug" color="var(--ac-red)" />
        ) : null}
        {state.kind === "loaded" && queues.length === 0 ? (
          <Notice text="Kein Prüfauftrag wartet, und keiner läuft." glyph="check" />
        ) : null}
      </div>

      {queues.length === 0 ? null : (
        <DataTable
          columns={[
            // „Kontrolle (ohne Modell)" ist die längste Zeilenform und gibt der
            // Spalte ihr Maß; ganz gelesen wird sie im Titel der Zelle.
            { key: "baseModel", label: "Basismodell", width: 220, maxWidth: 220 },
            { key: "endpoint", label: "Endpunkt", width: 130, maxWidth: 130 },
            {
              key: "slots",
              label: "Slots",
              hint: "Das wirksame Minimum aus den gleichzeitigen Aufrufen des Endpunkts und den Slots des Basismodells.",
              width: 64,
              align: "right",
            },
            // Je Art die wartenden. Der Hinweis am Kopf sagt, dass hier gewartet
            // wird — die Beschriftung allein läse sich sonst wie eine Gesamtzahl.
            ...REVIEW_JOB_KINDS.map((kind) => ({
              key: kind,
              label: KIND_LABELS[kind],
              hint: `Wartende Aufträge der Art „${KIND_LABELS[kind]}".`,
              width: 104,
              align: "right" as const,
            })),
            {
              key: "claimed",
              label: "laufend",
              hint: "Beansprucht, mit haltender Reservierung. Eine abgelaufene steht bei den wartenden.",
              width: 90,
              align: "right",
            },
            {
              key: "parked",
              label: "geparkt",
              hint: "Wartende Aufträge eines stillgelegten Agenten. Sie werden nicht gezogen, bis er freigegeben ist.",
              width: 90,
              align: "right",
            },
          ]}
          rows={queues.map((queue) => ({
            cells: {
              baseModel: (
                <StrongCell title={labelOfQueue(queue)}>{labelOfQueue(queue)}</StrongCell>
              ),
              endpoint: queue.endpointName ?? "—",
              slots: <NumericCell>{queue.slots === null ? "—" : formatNumber(queue.slots)}</NumericCell>,
              ...countsOf(queue),
              claimed: <NumericCell>{formatNumber(amountOf(queue.claimed))}</NumericCell>,
              parked: <NumericCell>{formatNumber(queue.parked)}</NumericCell>,
            },
          }))}
        />
      )}
    </ListSection>
  );
}

/**
 * Wie die Warteschlange in ihrer Zeile heißt.
 *
 * Ohne Endpunkt **und** ohne Basismodell ist es die Kontrolle — sie gehört zu
 * keinem Agenten und damit zu keinem Endpunkt (ADR-0010). Fehlt allein das
 * Basismodell, hat ein Agent noch nicht aufgelöst.
 */
function labelOfQueue(queue: QueueLoad): string {
  if (queue.baseModel !== null) {
    return queue.baseModel;
  }

  return queue.endpointId === null ? "Kontrolle (ohne Modell)" : "noch nicht aufgelöst";
}

/** Die wartenden je Art, als Zellen ihrer Spalten. */
function countsOf(queue: QueueLoad): Record<string, ReactNode> {
  const cells: Record<string, ReactNode> = {};

  for (const kind of REVIEW_JOB_KINDS) {
    cells[kind] = <NumericCell>{formatNumber(queue.pending[kind])}</NumericCell>;
  }

  return cells;
}

/** Die Summe über die Arten — „laufend" und „wartend" sind je eine Spalte. */
function amountOf(counts: Record<string, number>): number {
  return REVIEW_JOB_KINDS.reduce((total, kind) => total + (counts[kind] ?? 0), 0);
}
